import classNames from '@/common/utils/classNames';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useChatRecovery } from './useChatRecovery';
import { sendChatRecoveryEvent } from './events';
import { formatToman } from './types';

/**
 * The chat-recovery dock — the confirmed mockup (docs/work/ov-chat-recovery-mockup-2026-10-03.html,
 * «مک‌آپ ۴»): one informational surface is the hami system message (server-side);
 * this dock is the single interactive surface. One action, one surface — no
 * duplicated buttons in the thread.
 */
export function ChatRecoveryDock({
  chatId,
  active,
  onInteractiveChange,
}: {
  chatId: string | null;
  active: boolean;
  /** وقتی داک زنده است (پیشنهاد/شیت تأیید/خطا) — صفحهٔ چت ناوبریِ iframe را در همان لحظه‌ها ماسک می‌کند (مالک ۲۰۲۶-۱۰-۱۱). */
  onInteractiveChange?: (interactive: boolean) => void;
}) {
  const router = useRouter();
  const { state, flagOn, openConfirm, backFromConfirm, confirmSwitch, dismiss, switching } = useChatRecovery(chatId, active);
  const handedOffRef = useRef(false);

  const { phase, data } = state;
  const candidate = data.candidate;

  // در حالت embedded، کلیک روی لینک‌های saga داخل پیام‌های hami باعث ناوبریِ
  // iframe می‌شود (پلکِ لوتولی + ری‌لودِ hami). صفحهٔ چت آن لحظه‌ها را تا وقتی
  // داک زنده است با overlay می‌پوشاند — سیگنالش همین prop است.
  const interactive = active && flagOn && phase !== 'idle' && phase !== 'unavailable' && phase !== 'done';
  useEffect(() => {
    onInteractiveChange?.(interactive);
    return () => onInteractiveChange?.(false);
  }, [interactive, onInteractiveChange]);

  // Leaving the page after a completed hand-off (patient went to pay) is the
  // normal happy path — record it once so abandonment is measurable honestly.
  useEffect(() => {
    if (phase !== 'done' || handedOffRef.current) return;
    handedOffRef.current = true;
    sendChatRecoveryEvent('result_seen', { ...data, chat_id: chatId, outcome: 'handed_off' });
  }, [phase, data, chatId]);

  if (!active || !flagOn) return null;

  if (phase === 'done' && candidate) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-40 mx-auto w-full max-w-[430px] px-3 pb-4">
        <div className="rounded-2xl border border-[#bfe6d2] bg-[#e8f7ef] p-4 text-[13px] leading-7 text-[#155b38] shadow-lg">
          <b className="block text-sm">نوبت شما با پزشک جایگزین ثبت می‌شود</b>
          پرداخت با کیف پول را در صفحهٔ بعد کامل کنید ({formatToman(candidate.price_candidate)}؛
          مابقی در کیف پول شما می‌ماند).
          <button
            className="mt-3 w-full rounded-xl border border-[#bfe6d2] bg-white py-2.5 text-[12.5px] font-bold text-[#16a34a]"
            onClick={() => {
              if (data.handoffUrl) router.push(data.handoffUrl);
            }}
          >
            ادامهٔ ثبت نوبت با {candidate.name}
          </button>
        </div>
      </div>
    );
  }

  if (phase !== 'offered' && phase !== 'confirming' && phase !== 'failed') return null;
  if (!candidate || !data.doctor) return null;

  return (
    <>
      {/* confirmation sheet */}
      {phase === 'confirming' && (
        <div className="fixed inset-0 z-[60] flex items-end bg-[rgba(23,23,23,0.45)]" role="dialog" aria-modal>
          <div className="mx-auto w-full max-w-[430px] rounded-t-[22px] bg-white p-4">
            <h3 className="mb-2.5 text-[14.5px] font-bold">جایگزینی پزشک با {candidate.name}؟</h3>
            <ul className="mb-2.5 space-y-1">
              <li className="relative pr-4 text-[12.5px] leading-[2.1] text-[#415062]">
                نوبت فعلی با {data.doctor.name} لغو و <b>{formatToman(candidate.price_current)}</b> به کیف پول شما
                برمی‌گردد.
              </li>
              <li className="relative pr-4 text-[12.5px] leading-[2.1] text-[#415062]">
                نوبت جدید <b>{candidate.slot_text}</b> از همان کیف پول ثبت می‌شود (
                {formatToman(candidate.price_candidate)}؛ مابقی برای شما می‌ماند).
              </li>
            </ul>
            <div className="mb-3 rounded-[10px] bg-[#f9fafb] px-2.5 py-1.5 text-[11px] leading-[1.9] text-[#737373]">
              🔒 اگر ثبت نوبت جدید انجام نشود، وجه شما کامل در کیف پول می‌ماند.
            </div>
            {switching ? (
              <div className="rounded-xl bg-[#e8f0fc] py-3 text-center text-[13px] font-bold text-[#3861fb]">
                در حال انجام… لغو نوبت فعلی و آماده‌سازی نوبت جدید
              </div>
            ) : (
              <button
                className="w-full rounded-xl bg-[#16a34a] py-3 text-[13.5px] font-extrabold text-white"
                onClick={confirmSwitch}
              >
                تأیید و جایگزینی
              </button>
            )}
            {!switching && (
              <button
                className="mt-2 w-full bg-transparent text-[12px] text-[#a3a3a3]"
                onClick={backFromConfirm}
              >
                فعلاً نه
              </button>
            )}
          </div>
        </div>
      )}

      {/* dock */}
      <div className="fixed inset-x-0 bottom-0 z-40 mx-auto w-full max-w-[430px] border-t border-[#e5e7eb] bg-white px-3 py-3">
        <button
          aria-label="بستن — یعنی منتظر می‌مانم"
          className="absolute left-2.5 top-2 h-[26px] w-[26px] rounded-full border border-[#e5e7eb] bg-[#f9fafb] text-[13px] leading-none text-[#a3a3a3]"
          onClick={dismiss}
        >
          ×
        </button>
        {phase === 'failed' && (
          <div className="mb-2 rounded-lg bg-[#fdecec] px-2.5 py-1.5 text-[11.5px] text-[#dc2626]">
            لغو نوبت انجام نشد. دوباره تلاش کنید یا بعداً از «نوبت‌های من» لغو کنید.
          </div>
        )}
        <div className="flex items-center gap-2.5 rounded-2xl border border-[#e5e5e5] bg-white p-2.5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-base font-bold text-white">
            {candidate.name.replace('دکتر ', '').charAt(0)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-extrabold text-[#404040]">
              {candidate.name} · {candidate.expertise}
            </div>
            <div className="mt-0.5 text-[11px] leading-[1.8] text-[#737373]">
              {candidate.slot_text} · <b className="text-[#171717]">{formatToman(candidate.price_candidate)}</b>
            </div>
          </div>
          <button
            className="shrink-0 rounded-xl bg-[#3861fb] px-3.5 py-2.5 text-[12.5px] font-extrabold text-white hover:bg-[#2e54d9]"
            onClick={openConfirm}
          >
            جایگزینی پزشک
          </button>
        </div>
        <button
          className={classNames('mt-2 w-full bg-transparent text-[12px] text-[#dc2626]')}
          onClick={() => {
            sendChatRecoveryEvent('cancel_clicked', { chat_id: chatId, book_id: data.bookId });
            // Direct them to the existing cancel flow on the receipt page — the same
            // services, zero new code paths for plain cancellation.
            if (data.bookId) router.push(`/receipt/5532/${data.bookId}`);
          }}
        >
          یا لغو نوبت و استرداد وجه به کیف پول
        </button>
      </div>
    </>
  );
}
